import {
    DestroyRef,
    Injectable,
    OnDestroy,
    PLATFORM_ID,
    computed,
    inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, Subject, defer, from, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    IDesktopNotificationClickEvent,
    IDesktopNotificationPayload,
} from '@dxp/core/models';
import { USER_STORE } from '@dxp/core/stores';
import { AgentState, DesktopNotificationPermission } from '@dxp/core/types';
import { LoggerService } from '../logger/logger.service';

const DEFAULT_CXP_ICON = 'assets/images/cxp-signet.svg';
const DEFAULT_CXP_TITLE = 'Cxp';

@Injectable({
    providedIn: 'root',
})
export class DesktopNotificationService implements OnDestroy {
    readonly #destroyRef = inject(DestroyRef);
    readonly #platformId = inject(PLATFORM_ID);
    readonly #userStore = inject(USER_STORE);
    readonly #logger = inject(LoggerService);

    readonly #isBrowser: boolean;
    readonly #supportsNotification: boolean;

    readonly #permissionSubject =
        new BehaviorSubject<DesktopNotificationPermission>('unsupported');

    readonly permission$: Observable<DesktopNotificationPermission> =
        this.#permissionSubject.asObservable();

    readonly #clickSubject = new Subject<IDesktopNotificationClickEvent>();

    readonly click$: Observable<IDesktopNotificationClickEvent> =
        this.#clickSubject.asObservable();

    readonly #notifications = new Map<string, Notification>();

    readonly agentState = computed<AgentState>(() => {
        const state = this.#userStore.user().presence.state;

        switch (state) {
            case 'PendingBreak':
            case 'PendingLogout':
                return 'Busy';
            default:
                return state;
        }
    });

    constructor() {
        this.#isBrowser = isPlatformBrowser(this.#platformId);
        this.#supportsNotification =
            this.#isBrowser && typeof Notification !== 'undefined';

        if (!this.#supportsNotification) {
            this.#permissionSubject.next('unsupported');
            return;
        }

        this.#permissionSubject.next(Notification.permission);

        this.requestPermissionIfNeeded()
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
                next: permission => {
                    this.#logger.debug(
                        `Desktop Notifications are ${permission}`,
                    );
                },
                error: err => {
                    this.#logger.error(
                        'Desktop Notifications are disabled',
                        err,
                    );
                },
            });

        this.click$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(event => this.#handleNotificationClick(event));
    }

    ngOnDestroy() {
        this.#clearAllNotifications();
    }

    requestPermissionIfNeeded(): Observable<DesktopNotificationPermission> {
        if (!this.#supportsNotification) {
            return of('unsupported');
        }

        const current = this.#permissionSubject.value;

        if (current === 'granted' || current === 'denied') {
            return of(current);
        }

        return this.#requestPermission();
    }

    showNotification(payload: IDesktopNotificationPayload): string | null {
        if (!this.#canShowNotifications(payload)) {
            return null;
        }

        const shouldNotify =
            payload.notifyWhenFocused === true ||
            document.visibilityState === 'hidden';

        if (!shouldNotify) {
            return null;
        }

        try {
            const id = payload.id ?? uuidV4();
            const url = new URL(
                payload?.icon ?? DEFAULT_CXP_ICON,
                window.location.origin,
            );

            payload.icon = url.toString();
            payload.title = payload.title ?? DEFAULT_CXP_TITLE;

            const notification = new Notification(payload.title, {
                body: payload.body,
                icon: payload.icon,
                tag: payload.tag,
                data: payload.data,
                requireInteraction: payload.requireInteraction,
            });

            this.#notifications.set(id, notification);

            notification.onclick = () => {
                this.#clickSubject.next({
                    id,
                    tag: payload.tag,
                    data: payload.data,
                    callback: payload.callback,
                });

                notification?.close();
            };

            notification.onclose = () => {
                this.#notifications.delete(id);
                if (notification) {
                    notification.onclick = null;
                    notification.onclose = null;
                }
            };

            return id;
        } catch (err) {
            this.#logger.error(
                'Desktop Notification Service (Show Notification) -> Unable to show desktop notification',
                err,
            );

            return null;
        }
    }

    clearNotification(id: string) {
        const notification = this.#notifications.get(id);
        if (!notification) {
            return;
        }

        notification.close();
        this.#notifications.delete(id);
        notification.onclick = null;
        notification.onclose = null;
    }

    #clearAllNotifications() {
        for (const [id, notification] of this.#notifications.entries()) {
            notification.close();
            notification.onclick = null;
            notification.onclose = null;
            this.#notifications.delete(id);
        }
    }

    #requestPermission(): Observable<DesktopNotificationPermission> {
        if (!this.#supportsNotification) {
            return of('unsupported');
        }

        return defer(() => from(Notification.requestPermission())).pipe(
            tap(permission => {
                this.#permissionSubject.next(permission);
            }),
            catchError(err => {
                this.#logger.error(
                    'Desktop Notification Service (Request Permission) -> Failed to request notification permission',
                    err,
                );

                return of(this.#permissionSubject.value);
            }),
        );
    }

    #handleNotificationClick(event: IDesktopNotificationClickEvent) {
        if (document.visibilityState === 'hidden') {
            window.focus();
        }

        if (event.callback) {
            event.callback(event.id, event.tag, event.data);
        }
    }

    #canShowNotifications(payload: IDesktopNotificationPayload): boolean {
        if (
            payload.states &&
            payload.states.length > 0 &&
            !payload.states.includes(this.agentState())
        ) {
            return false;
        }

        return (
            this.#supportsNotification &&
            this.#permissionSubject.value === 'granted'
        );
    }
}
